<script setup lang="ts">
const {
  id = "",
  content = "",
  time = "",
  role,
  lineClamp = undefined,
  expandable = true,
  rating = false,
  richData = [],
} = defineProps<{
  id?: string;
  content?: string;
  time?: string;
  role: Role;
  lineClamp?: number;
  expandable?: boolean;
  rating?: boolean;
  richData?: IRichData[];
}>();

const emit = defineEmits(["like", "dislike"]);

const clampable = ref<HTMLElement | null>(null);

const { toggle } = useClampable(clampable, lineClamp);

const handleTextClick = (event: MouseEvent) => {
  const target = event.target as HTMLElement | null;
  const link = target?.closest("a") as HTMLAnchorElement | null;

  if (!link) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  window.open(link.href, "_blank", "noopener,noreferrer");
};

const classObject = computed(() => ({
  "is-user": role === "user",
  "is-assistant": role === "assistant",
}));
</script>

<template>
  <div
    class="chat-message"
    :class="classObject"
    @click="expandable ? toggle() : null"
  >
    <AssistantAvatar v-if="role === 'assistant'" class="avatar" />
    <div class="content">
      <div class="bubble">
        <slot>
          <!-- eslint-disable vue/no-v-html -->
          <div
            ref="clampable"
            class="text"
            @click="handleTextClick"
            v-html="content"
          ></div>
        </slot>
        <ChatRichData v-if="richData?.length" :items="richData" />
        <ChatMessageRating
          v-if="rating"
          :id="id"
          @like="emit('like')"
          @dislike="emit('dislike')"
        />
      </div>
      <div v-if="time" class="time">
        {{ time }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.chat-message {
  display: flex;
  flex-shrink: 0;
  flex-direction: row;
  align-items: flex-start;
  gap: 10px;
  font-size: 1rem;
  text-align: left;
  user-select: none; /* Standard syntax */

  .avatar {
    flex-shrink: 0;
    width: 30px;
    height: auto;
  }

  .content {
    display: flex;
    flex-direction: column;
    // Flex items default to min-width: auto (won't shrink below content's
    // natural width) - without this, long unbroken text/links widen the
    // whole message row instead of wrapping inside the bubble.
    min-width: 0;
    gap: 0.2em;
  }

  .bubble {
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 1em;
    gap: 1em;
    border-radius: var(--main-border-radius);
    font-size: 0.9em;
    padding: 0.75em;
    // min-width: 5em;
  }

  .text {
    min-width: 0;
    overflow-wrap: anywhere;
    user-select: text;

    :deep(a) {
      text-decoration: underline;
      overflow-wrap: anywhere;

      &:hover {
        opacity: 0.8;
      }
    }

    :deep(p) {
      margin: 1em 0;
    }

    :deep(ul),
    :deep(ol) {
      padding-left: 1.5em;
      margin: 1em 0;
    }

    :deep(li) {
      margin-bottom: 0.5em;
    }

    :deep(:first-child) {
      margin-top: 0;
    }

    :deep(:last-child) {
      margin-bottom: 0;
    }
  }

  .time {
    font-size: 0.7em;
    font-style: italic;
    opacity: 0.4;
  }

  &.is-assistant {
    align-self: flex-start;
    margin-right: 1em;

    .bubble {
      // border-radius: 0 20px 20px 20px;
      border-radius: 10px;
      background-color: var(--chat-bubble-assistant-background-color);
      color: var(--chat-bubble-assistant-text-color);

      :deep(a) {
        color: var(--chat-bubble-assistant-text-color);
      }
    }

    .time {
      text-align: left;
      margin-left: 0.5em;
    }
  }

  &.is-user {
    align-self: flex-end;
    margin-left: 1em;

    .bubble {
      // border-radius: 20px 0 20px 20px;
      border-radius: 10px;
      background-color: var(--chat-bubble-user-background-color);
      color: var(--chat-bubble-user-text-color);

      :deep(a) {
        color: var(--chat-bubble-user-text-color);
      }
    }

    .time {
      text-align: right;
      margin-right: 0.5em;
    }
  }

  &.is-clamped {
    .text {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }
}
</style>
